import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { TeamRole } from "../types";
import {
  Avatar,
  AvatarFallback,
  Badge,
  BodyText,
  Button,
  Card,
  HeadingText,
} from "../../../shared/ui";
import { formatPermissionLabel } from "../../../shared/lib";
import { Edit2, UserAdd } from "iconsax-reactjs";

interface RoleCardProps {
  role: TeamRole;
  onEditRole?: (role: TeamRole) => void;
  onAddMembers?: (role: TeamRole) => void;
  onDeleteRole?: (role: TeamRole) => void;
  onOpenDetails?: (role: TeamRole) => void;
}

const ROLE_DESCRIPTION_PREVIEW_WORDS = 16;

const truncateDescriptionByWords = (value: string, maxWords: number) => {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return value;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const RoleCard = ({
  role,
  onEditRole,
  onAddMembers,
  onDeleteRole,
  onOpenDetails,
}: RoleCardProps) => {
  const maxVisibleMembers = 4;
  const visibleMembers = role.members.slice(0, maxVisibleMembers);
  const remainingMembersCount = Math.max(
    role.members.length - maxVisibleMembers,
    0,
  );
  const permissionsPreviewCount = 1;
  const previewPermissions = role.permissions.slice(0, permissionsPreviewCount);
  const remainingPermissionsCount = Math.max(
    role.permissions.length - permissionsPreviewCount,
    0,
  );
  const roleDescriptionPreview = truncateDescriptionByWords(
    role.description,
    ROLE_DESCRIPTION_PREVIEW_WORDS,
  );

  return (
    <Card
      className="flex h-full cursor-pointer flex-col overflow-hidden rounded-[16px] bg-[#F7F8FD] transition-shadow hover:shadow-[0_2px_5px_rgba(15,20,40,0.12)] active:bg-[#EFF0FA]"
      onClick={() => onOpenDetails?.(role)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenDetails?.(role);
        }
      }}
    >
      <div className="flex items-center justify-between px-4 py-2.5">
        <HeadingText className="text-[18px] font-[450] leading-[24px] tracking-[-0.45px]">
          {role.name}
        </HeadingText>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className="rounded-md p-1 text-[#737AA8] hover:bg-white"
              aria-label={`More options for ${role.name}`}
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal size={18} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={12}
              onClick={(event) => event.stopPropagation()}
              className="min-w-[300px] rounded-[16px] border border-[#E1E6FF] bg-white p-1 shadow-[0_20px_45px_rgba(15,20,40,0.15)]"
            >
              <DropdownMenu.Item
                onSelect={(event) => {
                  event.stopPropagation();
                  onEditRole?.(role);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#2C2F44] outline-none transition-colors focus:bg-[#F5F6FD]"
              >
                <Edit2 size={18} className="text-[#6E7397]" />
                Edit role
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={(event) => {
                  event.stopPropagation();
                  onAddMembers?.(role);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#2C2F44] outline-none transition-colors focus:bg-[#F5F6FD]"
              >
                <UserAdd size={18} className="text-[#6E7397]" />
                Add members
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={(event) => {
                  event.stopPropagation();
                  onDeleteRole?.(role);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#E24B4B] outline-none transition-colors focus:bg-[#FFF1F1]"
              >
                <Trash2 size={18} className="text-[#E24B4B]" />
                Delete role
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <div className="m-0.5 flex flex-1 flex-col rounded-[16px] border border-[#EBEDF9] bg-white px-4 pt-4 pb-4">
        <BodyText
          className="line-clamp-3 min-h-[84px] max-w-[530px] text-[#383B56] text-[16px] font-[400] leading-[22px] tracking-[-0.42px]"
          title={role.description}
        >
          {roleDescriptionPreview}
        </BodyText>

        <section className="mt-0 lg:mt-4 2xl:mt-0">
          <BodyText className="my-2 font-[450] text-[#161827] text-[16px] tracking-[-0.21px] leading-[16px]">
            Members
          </BodyText>
          <div className="flex items-center -space-x-2">
            {visibleMembers.map((member) => (
              <Avatar
                key={`${role.id}-${member.id}`}
                className="size-10 shrink-0 bg-[#F3F4FB] ring-2 ring-white"
              >
                <AvatarFallback className="flex h-full w-full items-center justify-center rounded-full bg-[#F3F4FB] font-['Rubik'] text-[14px] font-[500] leading-6 tracking-[-0.4px] text-[#3B4060]">
                  {member.label}
                </AvatarFallback>
              </Avatar>
            ))}
            {remainingMembersCount > 0 ? (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EEF1FB] ring-2 ring-white font-['Rubik'] text-[16px] font-[500] leading-6 tracking-[-0.4px] text-[#3B4060]">
                +{remainingMembersCount}
              </div>
            ) : null}
            <Button
              variant="outline"
              size="icon"
              className="h-11 w-11 rounded-full border-dashed border-[#D9DEFF] text-[#737AA8]"
              onClick={(event) => {
                event.stopPropagation();
                onAddMembers?.(role);
              }}
            >
              <Plus size={16} />
            </Button>
          </div>
        </section>

        <section className="mt-auto pt-4">
          <BodyText className="mb-2 font-[450] text-[#161827] text-[16px] tracking-[-0.21px] leading-[16px]">
            Permissions
          </BodyText>
          <div className="flex flex-wrap gap-2">
            {previewPermissions.map((permission) => (
              <Badge
                key={`${role.id}-${permission}`}
                className="inline-flex max-w-[220px] truncate rounded-full font-['Rubik'] text-[14px] font-[350] leading-5 tracking-[-0.42px] text-[#161827] bg-[#F7F8FD]"
                title={formatPermissionLabel(permission)}
              >
                {formatPermissionLabel(permission)}
              </Badge>
            ))}
            {remainingPermissionsCount > 0 ? (
              <Badge className="rounded-full font-['Rubik'] text-[14px] font-[350] leading-5 tracking-[-0.42px] text-gray-10">
                +{remainingPermissionsCount} more
              </Badge>
            ) : null}
          </div>
        </section>
      </div>
    </Card>
  );
};

export default RoleCard;
